import { useState, useEffect } from 'react';
import { ArrowLeft, AlertCircle, Printer, Download, FileSpreadsheet, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { getUserProfile } from '../lib/auth';
import type { PhotoGeoMap } from '../lib/photoLocation';
import { useNotify } from './useNotify';
import { ReportHeader } from './report/ReportHeader';
import { ReportSummaryBar } from './report/ReportSummaryBar';
import { ReportControllerSection } from './report/ReportControllerSection';
import { ReportDoubleChecks } from './report/ReportDoubleChecks';
import { ReportPhotoIndex } from './report/ReportPhotoIndex';
import { InspectionTrailMap } from './InspectionTrailMap';
import type {
  Controller,
  ZoneIssue,
  DoubleCheckInspection,
  ControllerInspectionData,
  BrandSettingsData,
  ReportStyleSettings,
  AccountManagerData,
} from './report/types';
import { DEFAULT_REPORT_STYLE } from './report/types';

interface InspectionReportViewerProps {
  reportId: string;
  onBack: () => void;
}

interface ReportTree {
  report: Record<string, unknown> & { status: { status_type: string; label: string; color: string } | null };
  jobSite: Record<string, unknown> | null;
  controllers: Array<{
    id: string;
    controller_number: number;
    description: string;
    location: string;
    photo_url: string | null;
    water_start_times: unknown;
  }>;
  zoneInspections: Array<{
    id: string;
    controller_id: string | null;
    controller_number: number | null;
    zone_number: number;
    type: string | null;
    work_codes: string;
    notes: string;
    photo_urls: string[];
    photo_geo?: PhotoGeoMap | null;
  }>;
  zoneIssues: Array<{
    id: string;
    zone_inspection_id: string;
    work_code: string;
    notes: string;
    photo_urls: string[];
    photo_geo?: PhotoGeoMap | null;
  }>;
  controllerInspections: Array<{
    id: string;
    controller_id: string | null;
    controller_number: number | null;
    controller_description: string;
    controller_location: string;
    failure_codes: string[];
    notes: string;
    photo_urls: string[];
    photo_geo?: PhotoGeoMap | null;
  }>;
  doublecheckInspections: Array<{
    id: string;
    doublecheck_description: string;
    doublecheck_size: string | null;
    failure_codes: string[];
    notes: string;
    photo_urls: string[];
    photo_geo?: PhotoGeoMap | null;
  }>;
}

export function InspectionReportViewer({ reportId, onBack }: InspectionReportViewerProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<any>(null);
  const [jobSite, setJobSite] = useState<any>(null);
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [doubleCheckInspections, setDoubleCheckInspections] = useState<DoubleCheckInspection[]>([]);
  const [controllerInspectionsData, setControllerInspectionsData] = useState<ControllerInspectionData[]>([]);
  const [workCodes, setWorkCodes] = useState<Array<{ code: string; is_issue: boolean }>>([]);
  const [doubleCheckWorkCodes, setDoubleCheckWorkCodes] = useState<Array<{ code: string; is_issue: boolean }>>([]);
  const [brandSettings, setBrandSettings] = useState<BrandSettingsData>({ company_name: '', logo_url: null });
  const [styleSettings, setStyleSettings] = useState<ReportStyleSettings>({ ...DEFAULT_REPORT_STYLE });
  const [accountManager, setAccountManager] = useState<AccountManagerData | null>(null);
  // The GPS path panel is staff-only. The trail endpoint is Manager/Admin-gated
  // server-side, so asking for the role up front keeps a technician from
  // triggering a guaranteed 403 on every report they open.
  const [isManagerPlus, setIsManagerPlus] = useState(false);
  const [showTrail, setShowTrail] = useState(true);

  useEffect(() => {
    fetchReportData();
  }, [reportId]);

  useEffect(() => {
    let active = true;
    getUserProfile()
      .then((profile) => {
        if (active && profile) setIsManagerPlus(profile.role === 'Manager' || profile.role === 'Admin');
      })
      .catch(() => {
        /* best-effort: no role means no GPS panel, which is the safe default */
      });
    return () => {
      active = false;
    };
  }, []);

  const fetchReportData = async () => {
    try {
      const [tree, workCodesData, workCodeTypesData, brandData, themesData] = await Promise.all([
        apiClient.get<ReportTree>(`/api/reports/${reportId}/tree`),
        apiClient.get<Array<{ id: string; code: string; is_issue: boolean | null }>>('/api/work-codes'),
        apiClient.get<Array<{ work_code_id: string; type_name: string }>>('/api/work-code-types'),
        apiClient.get<{ company_name: string; logo_url: string | null }>('/api/brand-settings'),
        apiClient.get<Array<{ is_active: boolean; report_colors: Partial<ReportStyleSettings> | null }>>('/api/themes'),
      ]);

      setReport(tree.report);
      setJobSite(tree.jobSite);

      // Work codes for Zones and DoubleChecks are joined off the unified
      // work_codes table via work_code_types; the System Work Codes admin
      // page (JobCodesManagement) is the single SSOT for both.
      const idsByType = (typeName: string) =>
        new Set(workCodeTypesData.filter(t => t.type_name === typeName).map(t => t.work_code_id));
      const zoneTypeIds = idsByType('Zone');
      const doubleCheckTypeIds = idsByType('DoubleCheck');
      setWorkCodes(
        workCodesData
          .filter(wc => zoneTypeIds.has(wc.id))
          .map(wc => ({ code: wc.code, is_issue: wc.is_issue ?? false })),
      );
      setDoubleCheckWorkCodes(
        workCodesData
          .filter(wc => doubleCheckTypeIds.has(wc.id))
          .map(wc => ({ code: wc.code, is_issue: wc.is_issue ?? false })),
      );

      setBrandSettings({ company_name: brandData.company_name, logo_url: brandData.logo_url });

      const activeTheme = themesData.find(th => th.is_active);
      if (activeTheme?.report_colors) {
        setStyleSettings({ ...DEFAULT_REPORT_STYLE, ...activeTheme.report_colors });
      }

      // Assemble Controller[] tree from flat arrays
      const issuesByZoneInspection = new Map<string, ZoneIssue[]>();
      for (const issue of tree.zoneIssues) {
        if (!issuesByZoneInspection.has(issue.zone_inspection_id)) {
          issuesByZoneInspection.set(issue.zone_inspection_id, []);
        }
        issuesByZoneInspection.get(issue.zone_inspection_id)!.push({
          id: issue.id,
          work_code: issue.work_code,
          notes: issue.notes,
          photo_urls: issue.photo_urls ?? [],
          photo_geo: issue.photo_geo ?? null,
        });
      }

      // Group by controller_number (snapshotted on both controller_inspections
      // and zone_inspections) rather than the live controller_id, so a report
      // stays whole after its controller is deleted (controller_id goes null but
      // the snapshot survives). Identity comes from the controller_inspection
      // snapshot; the live controller, when it still exists, adds the photo and
      // water schedule (live-only fields that aren't snapshotted).
      const liveById = new Map(tree.controllers.map(c => [c.id, c]));
      const liveByNumber = new Map(tree.controllers.map(c => [c.controller_number, c]));
      const ciByNumber = new Map<number, ReportTree['controllerInspections'][number]>();
      for (const ci of tree.controllerInspections) {
        if (ci.controller_number != null) ciByNumber.set(ci.controller_number, ci);
      }
      // Stable block id per controller_number: the live id when the controller
      // still exists, else a synthetic key so deleted controllers still match
      // their controller-inspection data downstream.
      const blockId = (num: number) => liveByNumber.get(num)?.id ?? `removed-${num}`;

      const controllerMap = new Map<number, Controller>();
      const ensureController = (num: number): Controller => {
        const existing = controllerMap.get(num);
        if (existing) return existing;
        const live = liveByNumber.get(num);
        const snap = ciByNumber.get(num);
        const block: Controller = {
          id: blockId(num),
          controller_number: num,
          description: live?.description ?? snap?.controller_description ?? '',
          location: live?.location ?? snap?.controller_location ?? '',
          photo_url: live?.photo_url ?? null,
          water_start_times: (live?.water_start_times as Controller['water_start_times']) ?? null,
          zone_inspections: [],
        };
        controllerMap.set(num, block);
        return block;
      };

      for (const zi of tree.zoneInspections) {
        const num = zi.controller_number
          ?? (zi.controller_id ? liveById.get(zi.controller_id)?.controller_number ?? null : null);
        if (num == null) continue; // unattributable legacy orphan (pre-snapshot)
        ensureController(num).zone_inspections.push({
          id: zi.id,
          zone_number: zi.zone_number,
          type: zi.type ?? '',
          work_codes: zi.work_codes,
          notes: zi.notes ?? null,
          photo_urls: zi.photo_urls ?? [],
          photo_geo: zi.photo_geo ?? null,
          issues: issuesByZoneInspection.get(zi.id) ?? [],
        });
      }

      const sortedControllers = Array.from(controllerMap.values())
        .filter(c => c.zone_inspections.length > 0)
        .sort((a, b) => a.controller_number - b.controller_number);
      for (const c of sortedControllers) {
        c.zone_inspections.sort((a, b) => a.zone_number - b.zone_number);
      }
      setControllers(sortedControllers);

      setDoubleCheckInspections(
        tree.doublecheckInspections.map(dc => ({
          id: dc.id,
          description: dc.doublecheck_description,
          size: parseFloat(dc.doublecheck_size ?? '0'),
          failure_codes: dc.failure_codes ?? [],
          notes: dc.notes ?? '',
          photo_urls: dc.photo_urls ?? [],
          photo_geo: dc.photo_geo ?? null,
        })),
      );

      setControllerInspectionsData(
        tree.controllerInspections.map(ci => ({
          // Match the controller block by its snapshot number (via the same
          // blockId used above), so controller notes/failure codes still attach
          // after the live controller is deleted.
          controller_id: ci.controller_number != null ? blockId(ci.controller_number) : (ci.controller_id ?? ''),
          failure_codes: ci.failure_codes ?? [],
          notes: ci.notes ?? '',
          photo_urls: ci.photo_urls ?? [],
          photo_geo: ci.photo_geo ?? null,
        })),
      );

      // Account manager — non-critical, fetched after job site is known
      const aspirePropertyId = (tree.jobSite as any)?.aspire_property_id;
      if (aspirePropertyId) {
        try {
          const amData = await apiClient.get<{ account_manager: AccountManagerData }>(
            `/api/integrations/aspire/account-manager?propertyId=${aspirePropertyId}`,
          );
          setAccountManager(amData.account_manager ?? null);
        } catch {
          // Non-critical
        }
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = ' ';
    window.print();
    document.title = originalTitle;
  };

  const [exportingXlsx, setExportingXlsx] = useState(false);
  const handleExportExcel = async () => {
    if (!reportId || exportingXlsx) return;
    setExportingXlsx(true);
    try {
      const { blob, filename } = await apiClient.getBlob(`/api/reports/${reportId}/xlsx`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? `inspection_report_${reportId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      notify(tCommon('status.errorTitle'), t('viewer.errorExportExcel'), 'danger');
    } finally {
      setExportingXlsx(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-theme-secondary">{t('viewer.loadingReport')}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-surface rounded-xl shadow-sm border border-line p-12 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-theme-secondary mb-2">{t('viewer.notFoundTitle')}</h3>
          <p className="text-theme-secondary mb-6">{t('viewer.notFoundMessage')}</p>
          <button
            onClick={onBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            {t('viewer.backToReports')}
          </button>
        </div>
      </div>
    );
  }

  const totalControllers = controllers.length;
  const totalZones = controllers.reduce((sum, c) => sum + c.zone_inspections.length, 0);
  const zonesWithIssues = controllers.reduce(
    (sum, c) => sum + c.zone_inspections.filter(z => {
      if (z.issues && z.issues.length > 0) {
        return z.issues.some(issue => {
          const wc = workCodes.find(w => w.code === issue.work_code);
          return wc?.is_issue;
        });
      }
      return false;
    }).length,
    0,
  );
  const zonesWithPhotos = controllers.reduce(
    (sum, c) => sum + c.zone_inspections.filter(z => {
      const issuePhotos = z.issues ? z.issues.reduce((count, issue) => count + issue.photo_urls.length, 0) : 0;
      return z.photo_urls.length > 0 || issuePhotos > 0;
    }).length,
    0,
  );
  const totalDoubleChecks = doubleCheckInspections.length;
  const doubleChecksWithIssues = doubleCheckInspections.filter(dc => {
    if (dc.failure_codes.length === 0) return false;
    return dc.failure_codes.some(code => {
      const workCode = doubleCheckWorkCodes.find(wc => wc.code === code);
      return workCode?.is_issue === true;
    });
  }).length;

  return (
    <>
      <div className="max-w-7xl mx-auto">
        <div className="no-print flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-theme-secondary hover:text-theme-primary transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">{t('viewer.backToReports')}</span>
          </button>
          <div className="flex items-center gap-3">
            {report?.status?.status_type === 'approved' ? (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
              >
                <Download size={20} />
                <span>{t('viewer.downloadReport')}</span>
              </button>
            ) : null}
            <button
              onClick={handleExportExcel}
              disabled={exportingXlsx}
              className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              title={t('viewer.exportExcel')}
            >
              <FileSpreadsheet size={18} />
            </button>
            <button
              onClick={handlePrint}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title={t('viewer.printReport')}
            >
              <Printer size={18} />
            </button>
          </div>
        </div>

        {report?.rejection_reason && report?.status?.status_type === 'rejected' && (
          <div className="no-print bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-800 dark:text-red-300">{t('viewer.rejectedTitle')}</h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{report.rejection_reason as string}</p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-2">{t('viewer.rejectedHint')}</p>
              </div>
            </div>
          </div>
        )}

        <div id="report-content" className="report-document">
          <ReportHeader
            report={report}
            jobSite={jobSite}
            brandSettings={brandSettings}
            styleSettings={styleSettings}
            formatDate={formatDate}
            formatMonth={formatMonth}
            accountManager={accountManager}
            jobSiteId={(jobSite?.id as string | undefined) ?? (report?.job_site_id as string | undefined) ?? undefined}
          />

          <ReportSummaryBar
            totalControllers={totalControllers}
            totalZones={totalZones}
            zonesWithIssues={zonesWithIssues}
            zonesWithPhotos={zonesWithPhotos}
            totalDoubleChecks={totalDoubleChecks}
            doubleChecksWithIssues={doubleChecksWithIssues}
            styleSettings={styleSettings}
          />

          <div className="report-body">
            <h2 className="report-section-title" style={{ color: styleSettings.section_heading_color }}>
              {t('viewer.inspectionDetails')}
            </h2>

            <ReportControllerSection
              controllers={controllers}
              controllerInspectionsData={controllerInspectionsData}
              workCodes={workCodes}
              styleSettings={styleSettings}
            />

            <ReportDoubleChecks
              doubleCheckInspections={doubleCheckInspections}
              doubleCheckWorkCodes={doubleCheckWorkCodes}
              styleSettings={styleSettings}
            />

            <ReportPhotoIndex
              controllers={controllers}
              controllerInspectionsData={controllerInspectionsData}
              doubleCheckInspections={doubleCheckInspections}
              styleSettings={styleSettings}
            />
          </div>
        </div>

        {/* GPS path — deliberately OUTSIDE #report-content. The print stylesheet
            hides everything except #report-content, so this can never end up in
            the printed or downloaded copy a client receives, and the emailed PDF
            is rendered separately server-side. Staff-only view of where the tech
            actually walked. */}
        {isManagerPlus && (
          <div className="no-print bg-surface rounded-xl shadow-sm border border-line mt-6">
            <button
              onClick={() => setShowTrail((open) => !open)}
              className="w-full flex items-center justify-between p-4 hover:bg-subtle transition-colors"
            >
              <div className="flex items-center gap-3">
                <MapPin size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-theme-primary">{t('viewer.gpsPathSection')}</h3>
              </div>
              {showTrail ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {showTrail && (
              <div className="px-4 pb-4 border-t border-line pt-4">
                <p className="text-xs text-theme-secondary mb-1">{t('viewer.gpsPathHint')}</p>
                <p className="text-xs text-theme-secondary italic mb-3">{t('viewer.gpsPathInternalNote')}</p>
                <InspectionTrailMap reportId={reportId} />
              </div>
            )}
          </div>
        )}
      </div>

      {NotifyModal}
    </>
  );
}
